import dayjs from '@/lib/dayjs'
import { holidayLabelsById } from '@/lib/services/designs/designs.constants'
import { formatCurrency, formatNumber, parseCurrency } from '@/lib/utils'
import type { ConfigType } from 'dayjs'
import {
  PRICE_ADJUSTMENT_ROUNDING_NEAREST_DOLLAR,
  PRICE_ADJUSTMENT_ROUNDING_NEAREST_HALF_DOLLAR,
  PRICE_ADJUSTMENT_ROUNDING_NONE,
} from './priceAdjustments.schema'
import type {
  ActivePriceAdjustment,
  PriceAdjustmentEndType,
  PriceAdjustmentOperation,
  PriceAdjustmentRoundingMode,
  PriceAdjustmentScopeType,
  PriceAdjustmentStartType,
  PriceAdjustmentStatus,
  PriceAdjustmentType,
  PriceAdjustmentWhenType,
  ProductPriceAdjustment,
} from './priceAdjustments.types'

type NumberishValue = number | string | null | undefined

/** The schedule fields the summaries read; form drafts hold numbers as typed text. */
interface PriceAdjustmentSummarySchedule {
  is_enabled?: boolean
  is_yearly?: boolean
  suppressed_until_on?: string | null
  start_type?: PriceAdjustmentStartType | ''
  starts_on?: string | null
  start_holiday?: string | null
  start_offset_days?: NumberishValue
  end_type?: PriceAdjustmentEndType | ''
  ends_on?: string | null
  end_holiday?: string | null
  end_offset_days?: NumberishValue
  end_days_after_start?: NumberishValue
}

/**
 * The price change fields the summary helpers read. Saved price changes, timeline windows, the
 * price change in effect on products and collections, and form drafts each carry a subset.
 */
export interface PriceAdjustmentSummarySubject {
  id?: number
  name?: string | null
  status?: PriceAdjustmentStatus
  when_type?: PriceAdjustmentWhenType | ''
  scope_type?: PriceAdjustmentScopeType | ''
  adjustment_type?: PriceAdjustmentType | ''
  operation?: PriceAdjustmentOperation | ''
  /** A decimal string from the API; a form draft holds what the florist typed. */
  amount?: string | number
  rounding_mode?: PriceAdjustmentRoundingMode | ''
  priority?: number | string
  collection_ids?: (number | string)[]
  collections?: { id: number | string; name: string | null }[]
  next_boundary_at?: string | null
  active_window?: { starts_at: string | null } | null
  schedule?: PriceAdjustmentSummarySchedule
}

type PriceAdjustmentBadgeVariant = 'success' | 'info' | 'muted' | 'secondary' | 'warning'

export interface PriceAdjustmentBadgeMeta {
  label: string
  variant: PriceAdjustmentBadgeVariant
}

/** Price changes that start on the same day in the same scope, where only `winner` applies. */
export interface PriceAdjustmentSameStartNotice<TAdjustment extends PriceAdjustmentSummarySubject> {
  starts_at: string
  adjustments: TAdjustment[]
  adjustment_ids: number[]
  winner: TAdjustment
  winner_id: number | null
}

interface SameStartGroup<TAdjustment> {
  key: string
  adjustments: TAdjustment[]
}

const formatScheduleDate = (
  value: string | null | undefined,
  { includeYear = true }: { includeYear?: boolean } = {}
) => {
  if (!value) {
    return null
  }

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format(includeYear ? 'MMM D, YYYY' : 'MMM D') : null
}

const formatDayCount = (value: NumberishValue) => {
  const days = Number(value ?? 0)
  const absoluteDays = Math.abs(days)
  const dayLabel = absoluteDays === 1 ? 'day' : 'days'

  return { days, absoluteDays, dayLabel }
}

const capitalizeSentence = (value: string) => {
  if (!value) {
    return value
  }

  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

const normalizePositiveId = (id: NumberishValue) => {
  const normalized = Number(id)

  return Number.isFinite(normalized) && normalized > 0 ? normalized : null
}

const getHolidayLabel = (holidayKey: string | null | undefined) =>
  holidayKey ? holidayLabelsById[holidayKey] : undefined

const describeHolidayOffset = (
  holidayKey: string | null | undefined,
  offsetDays: NumberishValue
) => {
  const holidayName = getHolidayLabel(holidayKey) || 'selected holiday'
  const { days, absoluteDays, dayLabel } = formatDayCount(offsetDays)

  if (days === 0) {
    return `on ${holidayName}`
  }

  if (days < 0) {
    return `${absoluteDays} ${dayLabel} before ${holidayName}`
  }

  return `${absoluteDays} ${dayLabel} after ${holidayName}`
}

const getStartSummary = (schedule: PriceAdjustmentSummarySchedule | undefined) => {
  if (schedule?.start_type === 'fixed_date') {
    const startsOn = formatScheduleDate(schedule?.starts_on, {
      includeYear: !schedule?.is_yearly,
    })
    if (!startsOn) {
      return 'start date is not set'
    }

    if (schedule?.is_yearly) {
      return `starts ${startsOn} each year`
    }

    return `starts ${startsOn}`
  }

  if (schedule?.start_type === 'holiday_offset') {
    return `starts ${describeHolidayOffset(schedule?.start_holiday, schedule?.start_offset_days)}`
  }

  return 'start rule is incomplete'
}

const getEndSummary = (schedule: PriceAdjustmentSummarySchedule | undefined) => {
  const endType = schedule?.end_type || 'none'

  if (endType === 'none') {
    return 'has no automatic end date'
  }

  if (endType === 'fixed_date') {
    const endsOn = formatScheduleDate(schedule?.ends_on, {
      includeYear: !schedule?.is_yearly,
    })

    if (!endsOn) {
      return 'has an incomplete end date'
    }

    return schedule?.is_yearly ? `ends ${endsOn} each year` : `ends ${endsOn}`
  }

  if (endType === 'holiday_offset') {
    return `ends ${describeHolidayOffset(schedule?.end_holiday, schedule?.end_offset_days)}`
  }

  if (endType === 'days_after_start') {
    const { absoluteDays, dayLabel } = formatDayCount(schedule?.end_days_after_start)
    return `ends ${absoluteDays} ${dayLabel} after it starts`
  }

  return 'has an incomplete end rule'
}

const getActiveResetSummary = (schedule: PriceAdjustmentSummarySchedule | undefined) => {
  const endType = schedule?.end_type || 'none'

  if (endType === 'none') {
    return 'No automatic reset date.'
  }

  if (endType === 'fixed_date') {
    const endsOn = formatScheduleDate(schedule?.ends_on, {
      includeYear: !schedule?.is_yearly,
    })

    if (!endsOn) {
      return 'Auto-reset rule is incomplete.'
    }

    return schedule?.is_yearly ? `Auto-resets on ${endsOn} each year.` : `Auto-resets on ${endsOn}.`
  }

  if (endType === 'holiday_offset') {
    return `Auto-resets ${describeHolidayOffset(schedule?.end_holiday, schedule?.end_offset_days)}.`
  }

  if (endType === 'days_after_start') {
    const { absoluteDays, dayLabel } = formatDayCount(schedule?.end_days_after_start)
    return `Auto-resets ${absoluteDays} ${dayLabel} after start.`
  }

  return 'Auto-reset rule is incomplete.'
}

const formatAmount = (adjustment: PriceAdjustmentSummarySubject | null | undefined) => {
  const amount = parseCurrency(adjustment?.amount)

  if (adjustment?.adjustment_type === 'flat') {
    return formatCurrency(amount)
  }

  return `${amount}%`
}

const normalizeRoundingMode = (adjustment: PriceAdjustmentSummarySubject | null | undefined) =>
  adjustment?.rounding_mode || PRICE_ADJUSTMENT_ROUNDING_NEAREST_DOLLAR

const getPriceAdjustmentName = (adjustment: PriceAdjustmentSummarySubject | null | undefined) => {
  if (typeof adjustment?.name !== 'string') {
    return null
  }

  const normalizedName = adjustment.name.trim()
  return normalizedName || null
}

const getPriceAdjustmentHolidayLabel = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  const schedule = adjustment?.schedule

  return getHolidayLabel(schedule?.start_holiday) || getHolidayLabel(schedule?.end_holiday) || null
}

export const priceAdjustmentRepeatsAnnually = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  const startType = adjustment?.schedule?.start_type

  return (
    startType === 'holiday_offset' ||
    (startType === 'fixed_date' && adjustment?.schedule?.is_yearly)
  )
}

const suppressionResumeDateIsFuture = (suppressedUntilOn: string | null | undefined) => {
  if (!suppressedUntilOn) {
    return false
  }

  const resumeDate = dayjs(suppressedUntilOn, 'YYYY-MM-DD', true)

  return resumeDate.isValid() && dayjs().startOf('day').isBefore(resumeDate.startOf('day'))
}

export const priceAdjustmentIsSuppressed = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) =>
  adjustment?.schedule?.is_enabled !== false &&
  priceAdjustmentRepeatsAnnually(adjustment) &&
  suppressionResumeDateIsFuture(adjustment?.schedule?.suppressed_until_on)

const roundAdjustedPrice = (
  value: number,
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  const roundingMode = normalizeRoundingMode(adjustment)

  if (roundingMode === PRICE_ADJUSTMENT_ROUNDING_NONE) {
    return Number(value.toFixed(2))
  }

  if (roundingMode === PRICE_ADJUSTMENT_ROUNDING_NEAREST_HALF_DOLLAR) {
    return Number((Math.round(value * 2) / 2).toFixed(2))
  }

  return Math.round(value)
}

export const getPriceAdjustmentStatusMeta = (
  status: PriceAdjustmentStatus | null | undefined
): PriceAdjustmentBadgeMeta => {
  switch (status) {
    case 'active':
      return { label: 'Active', variant: 'success' }
    case 'scheduled':
      return { label: 'Scheduled', variant: 'info' }
    case 'expired':
      return { label: 'Expired', variant: 'muted' }
    case 'disabled':
      return { label: 'Disabled', variant: 'muted' }
    case 'draft':
    case null:
    case undefined:
    default:
      return { label: 'Draft', variant: 'secondary' }
  }
}

export const getPriceAdjustmentStateMeta = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
): PriceAdjustmentBadgeMeta | null => {
  if (priceAdjustmentIsSuppressed(adjustment)) {
    return { label: 'Skipped This Occurrence', variant: 'warning' }
  }

  if (adjustment?.status === 'disabled' || adjustment?.schedule?.is_enabled === false) {
    return { label: 'Disabled', variant: 'muted' }
  }

  return null
}

export const getPriceAdjustmentScopeLabel = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  if (adjustment?.scope_type === 'collections') {
    const collections = Array.isArray(adjustment?.collections)
      ? adjustment.collections.filter(Boolean)
      : []

    if (collections.length === 1) {
      return collections[0]?.name || 'Collection'
    }

    if (collections.length > 1) {
      return `${formatNumber(collections.length)} Collections`
    }

    const collectionIds = Array.isArray(adjustment?.collection_ids)
      ? adjustment.collection_ids.filter(Boolean)
      : []

    if (collectionIds.length > 1) {
      return `${formatNumber(collectionIds.length)} Collections`
    }

    return 'Selected Collection(s)'
  }

  return 'All Products'
}

export const getPriceAdjustmentGeneratedTitle = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  const scopeLabel = getPriceAdjustmentScopeLabel(adjustment)
  const holidayLabel = getPriceAdjustmentHolidayLabel(adjustment)

  return holidayLabel || scopeLabel
}

export const getPriceAdjustmentCardTitle = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  return getPriceAdjustmentName(adjustment) || getPriceAdjustmentGeneratedTitle(adjustment)
}

const getBoundaryTimestamp = (value: ConfigType) => {
  const parsed = value ? dayjs(value) : null

  return parsed?.isValid() ? parsed.valueOf() : null
}

export const getPriceAdjustmentUpcomingStartTimestamp = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined,
  now: ConfigType = null
) => {
  const nextBoundaryTimestamp = getBoundaryTimestamp(adjustment?.next_boundary_at)

  if (nextBoundaryTimestamp !== null) {
    return nextBoundaryTimestamp
  }

  const schedule = adjustment?.schedule

  if (schedule?.start_type !== 'fixed_date' || !schedule?.starts_on) {
    return null
  }

  const parsedStart = dayjs(schedule.starts_on)
  const nowDate = now ? dayjs(now) : dayjs()

  if (!parsedStart.isValid()) {
    return null
  }

  if (!schedule?.is_yearly) {
    if (parsedStart.endOf('day').isBefore(nowDate)) {
      return null
    }

    return parsedStart.valueOf()
  }

  let nextStart = parsedStart.year(nowDate.year())

  if (nextStart.endOf('day').isBefore(nowDate)) {
    nextStart = nextStart.add(1, 'year')
  }

  return nextStart.valueOf()
}

const getSameStartWinner = <TAdjustment extends PriceAdjustmentSummarySubject>(
  adjustments: TAdjustment[]
) =>
  [...adjustments].sort((left, right) => {
    const priorityDifference = Number(right?.priority ?? 0) - Number(left?.priority ?? 0)

    if (priorityDifference !== 0) {
      return priorityDifference
    }

    return Number(right?.id ?? 0) - Number(left?.id ?? 0)
  })[0] ?? null

const getPriceAdjustmentCollectionIds = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  const directCollectionIds = Array.isArray(adjustment?.collection_ids)
    ? adjustment.collection_ids
    : []
  const loadedCollectionIds = Array.isArray(adjustment?.collections)
    ? adjustment.collections.map((collection) => collection?.id)
    : []

  return [...directCollectionIds, ...loadedCollectionIds]
    .map(normalizePositiveId)
    .filter((collectionId) => collectionId !== null)
}

const formatPriceAdjustmentNoticeListLabel = (adjustments: PriceAdjustmentSummarySubject[]) => {
  const labels = adjustments.map(getPriceAdjustmentCardTitle).filter(Boolean)

  if (labels.length === 0) {
    return 'Price changes'
  }

  if (labels.length === 1) {
    return labels[0]
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`
  }

  return `${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}`
}

/** The price changes that can be in effect: saved ones and the summaries on collections. */
type ActivePriceAdjustmentCandidate = ProductPriceAdjustment | ActivePriceAdjustment

const getActivePriceAdjustmentStartTimestamp = (adjustment: ActivePriceAdjustmentCandidate) => {
  const startsAt = adjustment?.active_window?.starts_at

  if (!startsAt) {
    return 0
  }

  const timestamp = Date.parse(startsAt)

  return Number.isFinite(timestamp) ? timestamp : 0
}

const getWinningActivePriceAdjustment = (
  adjustments: (ActivePriceAdjustmentCandidate | null | undefined)[] | null | undefined
) =>
  [...(adjustments ?? [])]
    .filter((adjustment) => adjustment !== null && adjustment !== undefined)
    .sort((left, right) => {
      const priorityDifference = Number(right?.priority ?? 0) - Number(left?.priority ?? 0)

      if (priorityDifference !== 0) {
        return priorityDifference
      }

      const startDifference =
        getActivePriceAdjustmentStartTimestamp(right) - getActivePriceAdjustmentStartTimestamp(left)

      if (startDifference !== 0) {
        return startDifference
      }

      return Number(right?.id ?? 0) - Number(left?.id ?? 0)
    })[0] ?? null

const getSameStartGroups = <TAdjustment extends PriceAdjustmentSummarySubject>(
  adjustments: TAdjustment[]
) => {
  const groups: SameStartGroup<TAdjustment>[] = []
  const globalAdjustments = adjustments.filter((adjustment) => adjustment?.scope_type === 'global')

  if (globalAdjustments.length > 1) {
    groups.push({
      key: 'global',
      adjustments: globalAdjustments,
    })
  }

  const collectionGroupsById = new Map<number, TAdjustment[]>()

  adjustments
    .filter((adjustment) => adjustment?.scope_type === 'collections')
    .forEach((adjustment) => {
      getPriceAdjustmentCollectionIds(adjustment).forEach((collectionId) => {
        collectionGroupsById.set(collectionId, [
          ...(collectionGroupsById.get(collectionId) ?? []),
          adjustment,
        ])
      })
    })

  collectionGroupsById.forEach((collectionAdjustments, collectionId) => {
    if (collectionAdjustments.length > 1) {
      groups.push({
        key: `collection:${collectionId}`,
        adjustments: collectionAdjustments,
      })
    }
  })

  return groups.sort((left, right) => {
    const countDifference = right.adjustments.length - left.adjustments.length

    if (countDifference !== 0) {
      return countDifference
    }

    return left.key.localeCompare(right.key)
  })
}

export const getPriceAdjustmentUpcomingSameStartNotice = <
  TAdjustment extends PriceAdjustmentSummarySubject,
>(
  adjustments: TAdjustment[] | null | undefined,
  {
    monthsAhead = 12,
    now = null,
    startsAt = null,
  }: {
    /** How far ahead to look; `Infinity` looks at every upcoming start. */
    monthsAhead?: number
    now?: ConfigType
    /** Checks this start instead of the soonest one. */
    startsAt?: ConfigType
  } = {}
): PriceAdjustmentSameStartNotice<TAdjustment> | null => {
  const candidates = (adjustments ?? [])
    .filter((adjustment) => adjustment?.status === 'scheduled' || adjustment?.status === 'draft')
    .filter((adjustment) => adjustment?.schedule?.is_enabled !== false)
    .filter((adjustment) => getPriceAdjustmentUpcomingStartTimestamp(adjustment, now) !== null)

  if (candidates.length < 2) {
    return null
  }

  const targetTimestamp =
    getBoundaryTimestamp(startsAt) ??
    Math.min(
      ...candidates
        .map((adjustment) => getPriceAdjustmentUpcomingStartTimestamp(adjustment, now))
        .filter((timestamp) => timestamp !== null)
    )

  if (!Number.isFinite(targetTimestamp)) {
    return null
  }

  const nowDate = now ? dayjs(now) : dayjs()
  if (targetTimestamp < nowDate.startOf('day').valueOf()) {
    return null
  }

  if (Number.isFinite(monthsAhead)) {
    const latestUpcomingTimestamp = nowDate.add(monthsAhead, 'month').endOf('day').valueOf()

    if (targetTimestamp > latestUpcomingTimestamp) {
      return null
    }
  }

  const matchingAdjustments = candidates
    .filter(
      (adjustment) => getPriceAdjustmentUpcomingStartTimestamp(adjustment, now) === targetTimestamp
    )
    .sort((left, right) => Number(left?.id ?? 0) - Number(right?.id ?? 0))

  const sameStartGroup = getSameStartGroups(matchingAdjustments)[0]

  if (!sameStartGroup) {
    return null
  }

  const winner = getSameStartWinner(sameStartGroup.adjustments)

  if (!winner) {
    return null
  }

  return {
    starts_at:
      sameStartGroup.adjustments.find((adjustment) => adjustment?.next_boundary_at)
        ?.next_boundary_at ?? dayjs(targetTimestamp).toISOString(),
    adjustments: sameStartGroup.adjustments,
    adjustment_ids: sameStartGroup.adjustments
      .map((adjustment) => Number(adjustment?.id))
      .filter((adjustmentId) => adjustmentId > 0),
    winner,
    winner_id: Number(winner?.id ?? 0) || null,
  }
}

export const getPriceAdjustmentSameStartNoticeText = (
  notice: PriceAdjustmentSameStartNotice<PriceAdjustmentSummarySubject> | null | undefined,
  { dateLabel = null }: { dateLabel?: string | null } = {}
) => {
  if (!notice?.winner) {
    return ''
  }

  const prefix = dateLabel ? `On ${dateLabel}, ` : ''
  const adjustmentLabel = formatPriceAdjustmentNoticeListLabel(notice.adjustments ?? [])
  const winnerLabel = getPriceAdjustmentCardTitle(notice.winner)

  return `${prefix}${adjustmentLabel} start on the same day, so only ${winnerLabel} will be applied.`
}

export const getPriceAdjustmentSupportingTitle = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  const name = getPriceAdjustmentName(adjustment)
  const scopeLabel = getPriceAdjustmentScopeLabel(adjustment)
  const holidayLabel = getPriceAdjustmentHolidayLabel(adjustment)

  if (name) {
    return holidayLabel ? `${scopeLabel} (${holidayLabel})` : scopeLabel
  }

  return holidayLabel ? scopeLabel : null
}

export const getPriceAdjustmentActionLabel = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  const direction = adjustment?.operation === 'decrease' ? 'Decrease' : 'Increase'
  return `${direction} prices by ${formatAmount(adjustment)}`
}

export const getPriceAdjustmentSignedAmountLabel = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  const prefix = adjustment?.operation === 'decrease' ? '-' : '+'
  return `${prefix}${formatAmount(adjustment)}`
}

export const getPriceAdjustmentScheduleSummary = (
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  const schedule = adjustment?.schedule

  if (priceAdjustmentIsSuppressed(adjustment)) {
    return `Skipped for this occurrence. ${capitalizeSentence(getStartSummary(schedule))} and ${getEndSummary(schedule)}.`
  }

  if (!schedule?.is_enabled) {
    if (adjustment?.when_type === 'scheduled') {
      return `Disabled until re-enabled. ${capitalizeSentence(getStartSummary(schedule))} and ${getEndSummary(schedule)}.`
    }

    return 'Disabled until re-enabled.'
  }

  if (adjustment?.status === 'active') {
    return `Active now. ${getActiveResetSummary(schedule)}`
  }

  return `${capitalizeSentence(getStartSummary(schedule))} and ${getEndSummary(schedule)}.`
}

export const getAdjustedPricePreview = (
  basePrice: NumberishValue,
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  const baseAmount = parseCurrency(basePrice)
  const amount = parseCurrency(adjustment?.amount)
  const delta = adjustment?.adjustment_type === 'flat' ? amount : baseAmount * (amount / 100)
  const nextPrice = adjustment?.operation === 'decrease' ? baseAmount - delta : baseAmount + delta

  return formatCurrency(roundAdjustedPrice(nextPrice, adjustment))
}

/** A collection picked on the product form, with the price change in effect for it. */
interface DraftProductCollection {
  id?: NumberishValue
  active_price_adjustment?: ActivePriceAdjustment | null
}

/** The price change a product would get from its picked collections, before it is saved. */
export const getDraftProductActivePriceAdjustment = ({
  collections = [],
  priceAdjustments = [],
}: {
  collections?: (DraftProductCollection | null | undefined)[] | null
  priceAdjustments?: ProductPriceAdjustment[] | null
} = {}) => {
  const activeAdjustments = (priceAdjustments ?? []).filter(
    (adjustment) => adjustment?.status === 'active'
  )
  const selectedCollectionIds = new Set(
    (collections ?? [])
      .map((collection) => normalizePositiveId(collection?.id))
      .filter((collectionId) => collectionId !== null)
  )
  const collectionAdjustmentsFromSelections = (collections ?? [])
    .map((collection) => collection?.active_price_adjustment)
    .filter(Boolean)
  const collectionAdjustmentsFromIndex = activeAdjustments.filter(
    (adjustment) =>
      adjustment?.scope_type === 'collections' &&
      getPriceAdjustmentCollectionIds(adjustment).some((collectionId) =>
        selectedCollectionIds.has(collectionId)
      )
  )
  const winningCollectionAdjustment = getWinningActivePriceAdjustment([
    ...collectionAdjustmentsFromSelections,
    ...collectionAdjustmentsFromIndex,
  ])

  if (winningCollectionAdjustment) {
    return winningCollectionAdjustment
  }

  return getWinningActivePriceAdjustment(
    activeAdjustments.filter((adjustment) => adjustment?.scope_type === 'global')
  )
}

export const getActivePriceAdjustmentPreviewLabel = (
  basePrice: NumberishValue,
  adjustment: PriceAdjustmentSummarySubject | null | undefined
) => {
  return `Price change active. Effective price: ${getAdjustedPricePreview(basePrice, adjustment)}.`
}
